import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Search from '@ingka/search';
import ListView, { ListViewItem } from '@ingka/list-view';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody, ModalFooter } from '@ingka/modal';
import { useDebounceValue } from 'usehooks-ts';
import { LoadingState } from '@/components/helpers/loading';
import { GenericErrorCard } from '@/components/data/error';
import Button from '@ingka/button';
import { useContentTracking } from '@/lib/hooks/tracking';
import {
  useActiveLocation,
  useShoppingBag,
} from '@/redux/features/shopping-bag/hooks';
import {
  useWebshopMode,
  WebshopModeProvider,
} from '@/components/page-widgets/webshop/context';
import WarningIcon from '@ingka/ssr-icon/paths/warning-triangle';
import InlineMessage from '@ingka/inline-message';
import { getHomePath } from '@/lib/utils/router';
import { useRouter } from '@/lib/router';
import { useUserLocations } from '@/redux/features/session/hooks';
import { getPrimaryCdsLocation } from '@/lib/utils/locations';
import { OrderType } from '@/redux/back-dashboard-api';

const formatCdsLocation = (
  cdsLocation: { name: string; primary: boolean }[]
) => {
  if (!cdsLocation.length) return '';

  const primary = getPrimaryCdsLocation(cdsLocation);
  const others = cdsLocation
    .filter((entry) => entry !== primary)
    .map((entry) => entry.name);

  return (
    (primary?.name || '') + (others.length ? ` (${others.join(', ')})` : '')
  );
};

const FindLocationSiderContent: FC<
  ModalComponentBaseProps & {
    onBack?: () => void;
  }
> = ({ onBack, handleDismiss, ...rest }) => {
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();
  const { setLocation, resetBag } = useWebshopMode();
  const router = useRouter();
  // Data
  const shoppingBag = useShoppingBag();
  const { data: locations = [], isLoading, error } = useUserLocations();
  const { data: location } = useActiveLocation();
  const [search, setSearch] = useDebounceValue('', 300);
  const [selectedLocation, setSelectedLocation] = useState(location?._id || '');
  // Event handlers
  const onSelectLocation = (locationId: string) => {
    setLocation(locationId);
    resetBag();

    // Force redirection to the home page as "activeLocation" is used in multiple places
    router.push(getHomePath());
    handleDismiss();
  };

  useEffect(() => {
    trackContent('find-location', { type: 'sider' });
  }, []);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) return <GenericErrorCard className="pt-6" error={error} card />;

  return (
    <Sheets
      {...rest}
      data-testid="findLocationSider"
      aria-label={t('page.findLocation.title')}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.findLocation.title')}
          backBtnClick={onBack}
        />
      }
      footer={
        <ModalFooter>
          <Button
            data-testid="confirmLocationBtn"
            type="primary"
            disabled={!selectedLocation}
            onClick={() => onSelectLocation(selectedLocation)}
          >
            {t('label.confirm')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalBody>
        {shoppingBag.shoppingItems.length > 0 && (
          <InlineMessage
            className="mb-6"
            ssrIcon={WarningIcon}
            title={t('page.findLocation.bagNotEmptyTitle')}
            body={t('page.findLocation.bagNotEmptyDescription')}
            variant="cautionary"
          />
        )}

        <div className="pb-5">
          <Search
            ariaLabel={t('aria.search')}
            clearBtnText={t('aria.clearSearch')}
            id="search"
            onSearch={(_, context) => {
              setSearch(context.value || '');
            }}
            placeholder={t('label.search')}
            onChange={(e) => {
              setSearch(e.target.value || '');
            }}
            submitBtnText={t('aria.submit')}
          />
        </div>

        <ListView id="locationList">
          {locations
            .filter((location) => {
              if (!search) return true;

              const searchText = `${location._id}:${location.name}:${
                location.atkomstClientId
              }:${location.cdsLocation
                ?.map((entry) => entry.name)
                .join(':')}`.toLowerCase();

              return searchText.includes(search.toLowerCase());
            })
            .map((entry) => (
              <ListViewItem
                key={entry._id}
                emphasised={true}
                control="radiobutton"
                title={entry.name}
                description={formatCdsLocation(entry.cdsLocation || [])}
                controlProps={{
                  value: entry._id,
                  checked: entry._id === selectedLocation,
                }}
                onChange={() => setSelectedLocation(entry._id)}
              />
            ))}
        </ListView>
      </ModalBody>
    </Sheets>
  );
};

const FindLocationSider: FC<
  ModalComponentBaseProps & {
    onBack?: () => void;
  }
> = (props) => {
  return (
    <WebshopModeProvider orderType={OrderType.Order}>
      <FindLocationSiderContent {...props} />
    </WebshopModeProvider>
  );
};

export default FindLocationSider;
